import { Article, SyncArticle } from "../types/article";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { isLocalCombinationArticle } from "../Helpers/articleHelpers";
import { LocalCombinationItem } from "@coworker/apprestructured/src/shared/types/localCombinationItem";
import { ItemType } from "@coworker/apprestructured/src/shared/constants/itemType";

export function mapCombinationArticlesByNo(
  combinationArticlesData: LocalCombinationItem[]
) {
  return combinationArticlesData?.reduce(
    (acc: Record<string, Record<string, number>>, combi) => {
      acc[combi?.no] =
        combi.itemsInCombination?.reduce(
          (items: Record<string, number>, art) => {
            items[art.item_no] = art.quantity;
            return items;
          },
          {}
        ) ?? {};
      return acc;
    },
    {}
  );
}

type GroupedArticle = {
  productArticleId: string;
  articlesCountInCombination?: number;
};

type GroupedData = Record<string, GroupedArticle[]>;

export function mapGroupedCombinationArticles(
  grouped: GroupedData
): Record<string, Record<string, number>> {
  const normalized: Record<string, Record<string, number>> = {};
  for (const [parentId, articles] of Object.entries(grouped)) {
    normalized[parentId] = {};
    for (const art of articles) {
      normalized[parentId][art.productArticleId] =
        art.articlesCountInCombination ?? 1;
    }
  }
  return normalized;
}

export function diffCombinations(
  latestCombinationArticlesData: Item[],
  combinationArticlesData: Record<string, Record<string, number>>,
  groupedData: Record<string, Record<string, number>>,
  combinationArticlesCollection: Article[],
  articlesInCombination: Article[]
) {
  const toAdd: SyncArticle[] = [];
  const toUpdate: Article[] = [];
  const toDelete: { id: string }[] = [];

  const articleIndex = new Map<string, Article[]>();
  const addToIndex = (article: Article) => {
    if (!articleIndex.has(article.productArticleId)) {
      articleIndex.set(article.productArticleId, []);
    }
    articleIndex.get(article.productArticleId)!.push(article);
  };

  [...articlesInCombination, ...combinationArticlesCollection].forEach(
    addToIndex
  );

  const findArticles = (itemNo: string): Article[] =>
    articleIndex.get(itemNo) || [];

  const parentInfoMap = new Map(
    combinationArticlesCollection
      .filter((a) =>
        isLocalCombinationArticle(a.productArticleType as ArticleType)
      )
      .map((a) => [
        a.productArticleId,
        { parentQty: a.nbrArticles || 1, mediaId: a.mediaId },
      ])
  );

  for (const parentId of Object.keys(combinationArticlesData)) {
    const combiItems = combinationArticlesData[parentId] || {};
    const groupedItems = groupedData[parentId] || {};
    const parentInfo = parentInfoMap.get(parentId);

    for (const [itemNo, qty] of Object.entries(combiItems)) {
      const existingQty = groupedItems[itemNo];

      if (existingQty == null) {
        // Articles to Add
        toAdd.push({
          parentId,
          parentQty: parentInfo?.parentQty || 1,
          itemNo,
          quantity: qty,
          mediaId: parentInfo?.mediaId || "",
          articlesCountInCombination: qty,
        });
      } else if (existingQty !== qty) {
        // Article to Update
        for (const art of findArticles(itemNo)) {
          toUpdate.push({
            ...art,
            groupId: art.groupId ?? "",
            groupName: art.groupName ?? "",
            nbrArticles: qty,
            articlesCountInCombination: qty,
          });
        }
      }
    }

    // Articles to Delete
    for (const itemNo of Object.keys(groupedItems)) {
      if (!(itemNo in combiItems)) {
        for (const art of findArticles(itemNo)) {
          if (art?.id) toDelete.push({ id: art.id });
        }
      }
    }

    // Update main article if sub-articles have changed
    if (toAdd.length > 0 || toDelete.length > 0) {
      for (const parentArticle of findArticles(parentId)) {
        toUpdate.push({
          ...parentArticle,
          groupId: parentArticle.groupId ?? "",
          groupName: parentArticle.groupName ?? "",
          articlesCountInCombination: Object.keys(combiItems).length,
        });
      }
    }
  }
  // Handle local combination main articles that have no sub-articles or later have sub-articles
  const parentNameMap = new Map<string, string>(
    (latestCombinationArticlesData || []).map((a: any) => [a.no, a.name])
  );

  const allParentIds = new Set<string>([
    ...Object.keys(combinationArticlesData),
    ...Object.keys(groupedData),
  ]);

  for (const parentId of allParentIds) {
    const combiItems = combinationArticlesData[parentId];
    const parentArticles = findArticles(parentId);
    if (!parentArticles || parentArticles.length === 0) continue;

    const newCount =
      combiItems && Object.keys(combiItems).length > 0
        ? Object.keys(combiItems).length
        : 0;

    const newName =
      newCount > 0
        ? (parentNameMap.get(parentId) ?? parentArticles[0]?.itemName ?? "")
        : "Unknown";

    // Only push an update if either name or count actually changed — prevents no-op updates
    for (const parentArticle of parentArticles) {
      if (
        parentArticle.itemName !== newName ||
        parentArticle.articlesCountInCombination !== newCount
      ) {
        const foundArticleInCombination = articlesInCombination.find(
          (a) => a.productArticleId === parentId
        );

        toUpdate.push({
          ...parentArticle,
          itemName: newName,
          productArticleType: foundArticleInCombination
            ? parentArticle.productArticleType
            : "SCI",
          articlesCountInCombination: newCount,
        });
      }
    }
  }

  return { toAdd, toUpdate, toDelete };
}

export async function enrichToAddArticles(
  toAdd: SyncArticle[],
  combinationArticlesCollection: Article[]
): Promise<Partial<Article>[]> {
  if (toAdd.length === 0) return [];

  const itemsInfo = await fetchItemsInfo(
    toAdd.map((item) => item.itemNo).join(",")
  );

  return itemsInfo.flatMap((info: Item) => {
    const addItem = toAdd.find((i) => i.itemNo === info.no);
    if (!addItem) return [];

    const matchingParents = combinationArticlesCollection.filter(
      (art) =>
        art.productArticleId === addItem.parentId &&
        art.productArticleType === ItemType.LOCAL_COMBINATION
    );

    return matchingParents.map(
      (parent): Partial<Article> => ({
        productArticleId: info.no,
        productArticleType: info.type,
        hfb: info.hfbNo || "Unknown",
        itemName: info.name || "Unknown",
        itemType: info.description ?? "Unknown",
        itemColor: info.description?.split(",")?.[1] || "Unknown",
        imageSmall: info.smallImageUrl || info.imageUrl || "Unknown",
        parentArticleCombinationNumber: parent.productArticleId,
        nbrArticles: (parent.nbrArticles || 1) * addItem.quantity,
        groupId: parent.groupId ?? "",
        groupName: parent.groupName ?? "",
        mediaId: parent.mediaId || "",
        articlesCountInCombination: addItem.quantity,
        active: "ACTIVE",
      })
    );
  });
}
